import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cx } from './cx.ts';

/**
 * Calendar-based date-range filter (usage/requests pages). Two-month
 * grid inside a Radix popover with caller-supplied preset shortcuts.
 * The component works entirely in LOCAL `YYYY-MM-DD` day strings — the
 * caller owns the conversion to ISO instants and all i18n copy (this
 * layer takes labels as props, per Deck component conventions).
 *
 * Selection flow: first click anchors, second click completes (order
 * is normalised), the panel closes. Days after `today` are always
 * disabled; once an anchor is set, days putting the inclusive span
 * over `maxSpanDays` are disabled too.
 */

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export interface DateRangePreset {
  readonly id: string;
  readonly label: string;
  readonly range: DateRange;
}

interface Props {
  readonly value: DateRange;
  readonly onChange: (range: DateRange, presetId: string | null) => void;
  readonly presets: readonly DateRangePreset[];
  readonly activePresetId: string | null;
  /** Inclusive span cap in days (endpoint days count). */
  readonly maxSpanDays?: number;
  /** Local `YYYY-MM-DD`; injectable for tests. */
  readonly today?: string;
  readonly locale?: string;
  readonly ariaLabel: string;
  readonly labels: { readonly prevMonth: string; readonly nextMonth: string };
  readonly testId?: string;
  readonly className?: string;
}

export function DateRangePicker({
  value,
  onChange,
  presets,
  activePresetId,
  maxSpanDays = 90,
  today,
  locale,
  ariaLabel,
  labels,
  testId,
  className,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // Right-hand month of the two-month view, as a month index.
  const [viewMonth, setViewMonth] = useState(() => monthIndexOfDay(value.to));

  const todayDay = today ?? formatDay(new Date());
  const activePreset = presets.find((preset) => preset.id === activePresetId);

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    setAnchor(null);
    setHovered(null);
    if (next) setViewMonth(monthIndexOfDay(value.to));
  }

  function handleDayClick(day: string): void {
    if (anchor === null) {
      setAnchor(day);
      return;
    }
    const range = anchor <= day ? { from: anchor, to: day } : { from: day, to: anchor };
    onChange(range, null);
    setOpen(false);
    setAnchor(null);
    setHovered(null);
  }

  function isDayDisabled(day: string): boolean {
    if (day > todayDay) return true;
    if (anchor !== null && Math.abs(diffDays(anchor, day)) >= maxSpanDays) return true;
    return false;
  }

  // Preview the pending range while the second endpoint is being chosen.
  const previewFrom = anchor !== null ? minDay(anchor, hovered ?? anchor) : value.from;
  const previewTo = anchor !== null ? maxDay(anchor, hovered ?? anchor) : value.to;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger
        aria-label={ariaLabel}
        data-testid={testId}
        className={cx(
          'inline-flex h-9 items-center gap-2 rounded border border-line bg-well px-3 text-dense text-paper transition-colors duration-120 hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/20 data-[state=open]:border-signal-deep',
          className,
        )}
      >
        <CalendarDays aria-hidden="true" className="h-3.5 w-3.5 text-paper-low" />
        {activePreset !== undefined ? (
          <span>{activePreset.label}</span>
        ) : (
          <span className="font-mono">
            {value.from} → {value.to}
          </span>
        )}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className="z-50 rounded-lg border border-line bg-raised p-3 shadow-overlay animate-panel-in"
        >
          <div className="flex gap-4">
            <div className="flex min-w-28 flex-col gap-1 border-r border-line-soft pr-3">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => {
                    onChange(preset.range, preset.id);
                    setOpen(false);
                    setAnchor(null);
                  }}
                  className={cx(
                    'rounded px-2.5 py-1.5 text-left text-dense transition-colors duration-120 hover:bg-active hover:text-paper',
                    preset.id === activePresetId ? 'bg-signal-dim text-signal' : 'text-paper-mid',
                  )}
                  data-testid={testId !== undefined ? `${testId}-preset-${preset.id}` : undefined}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  aria-label={labels.prevMonth}
                  onClick={() => setViewMonth((current) => current - 1)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper"
                >
                  <ChevronLeft aria-hidden="true" className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={labels.nextMonth}
                  onClick={() => setViewMonth((current) => current + 1)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper"
                >
                  <ChevronRight aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>
              <div className="flex gap-4">
                {[viewMonth - 1, viewMonth].map((monthIndex) => (
                  <MonthGrid
                    key={monthIndex}
                    monthIndex={monthIndex}
                    locale={locale}
                    previewFrom={previewFrom}
                    previewTo={previewTo}
                    anchor={anchor}
                    isDayDisabled={isDayDisabled}
                    onDayClick={handleDayClick}
                    onDayHover={setHovered}
                  />
                ))}
              </div>
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function MonthGrid(props: {
  readonly monthIndex: number;
  readonly locale: string | undefined;
  readonly previewFrom: string;
  readonly previewTo: string;
  readonly anchor: string | null;
  readonly isDayDisabled: (day: string) => boolean;
  readonly onDayClick: (day: string) => void;
  readonly onDayHover: (day: string | null) => void;
}): JSX.Element {
  const { monthIndex, locale } = props;
  const year = Math.floor(monthIndex / 12);
  const month = monthIndex - year * 12; // 0-based
  const monthTitle = useMemo(
    () => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(new Date(year, month, 1)),
    [locale, year, month],
  );
  const weekdayNames = useMemo(() => weekdayHeaders(locale), [locale]);

  const firstDay = new Date(year, month, 1);
  // Monday-start grid: JS getDay() is 0=Sunday.
  const leadingBlanks = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return (
    <div className="flex w-[15.5rem] flex-col gap-1.5">
      <p className="m-0 text-center text-label text-paper-mid">{monthTitle}</p>
      <div className="grid grid-cols-7 gap-y-0.5" onMouseLeave={() => props.onDayHover(null)}>
        {weekdayNames.map((name, index) => (
          <span
            key={`${name}-${index}`}
            aria-hidden="true"
            className="flex h-7 items-center justify-center text-micro text-paper-faint"
          >
            {name}
          </span>
        ))}
        {Array.from({ length: leadingBlanks }, (_, index) => (
          <span key={`blank-${index}`} aria-hidden="true" />
        ))}
        {Array.from({ length: daysInMonth }, (_, index) => {
          const day = formatDayParts(year, month + 1, index + 1);
          const inRange = day >= props.previewFrom && day <= props.previewTo;
          const isEndpoint = day === props.previewFrom || day === props.previewTo || day === props.anchor;
          const disabled = props.isDayDisabled(day);
          return (
            <button
              key={day}
              type="button"
              aria-label={day}
              disabled={disabled}
              onClick={() => props.onDayClick(day)}
              onMouseEnter={() => props.onDayHover(day)}
              className={cx(
                'flex h-7 items-center justify-center rounded font-mono text-dense transition-colors duration-120',
                disabled
                  ? 'cursor-default text-paper-faint opacity-40'
                  : 'text-paper-mid hover:bg-active hover:text-paper',
                !disabled && inRange && !isEndpoint && 'bg-signal-dim text-signal',
                !disabled && isEndpoint && 'bg-signal text-signal-ink hover:bg-signal-bright hover:text-signal-ink',
              )}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Localised Monday-first weekday headers (narrow form). */
function weekdayHeaders(locale: string | undefined): readonly string[] {
  const format = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
  // 2024-01-01 is a Monday.
  return Array.from({ length: 7 }, (_, index) => format.format(new Date(2024, 0, 1 + index)));
}

function monthIndexOfDay(day: string): number {
  const year = Number.parseInt(day.slice(0, 4), 10);
  const month = Number.parseInt(day.slice(5, 7), 10);
  return year * 12 + (month - 1);
}

function formatDay(date: Date): string {
  return formatDayParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

function formatDayParts(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Whole-day difference between two local `YYYY-MM-DD` strings. */
function diffDays(a: string, b: string): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((dayToLocalDate(b).getTime() - dayToLocalDate(a).getTime()) / MS_PER_DAY);
}

function dayToLocalDate(day: string): Date {
  return new Date(
    Number.parseInt(day.slice(0, 4), 10),
    Number.parseInt(day.slice(5, 7), 10) - 1,
    Number.parseInt(day.slice(8, 10), 10),
  );
}

function minDay(a: string, b: string): string {
  return a <= b ? a : b;
}

function maxDay(a: string, b: string): string {
  return a <= b ? b : a;
}
